<template>
  <div class="topbar">
    <div class="menu-toggle" @click="toggleSidebar">
      <i class="bi bi-list"></i>
    </div>
    <div class="topbar-title">
      <h2>{{ pageTitle }}</h2>
    </div>
    
    <!-- Sección de acciones del usuario -->
    <div class="topbar-actions">
      <!-- Botón de ayuda/tour -->
      <div class="help-menu" @click="toggleHelpDropdown">
        <i class="bi bi-question-circle help-icon"></i>
        <div class="help-dropdown" :class="{ 'show': showHelpDropdown }">
          <div class="dropdown-item" @click.stop="startTour">
            <i class="bi bi-play-circle"></i>
            Iniciar Tour Guiado
          </div>
          <div class="dropdown-item" @click.stop="resetTour">
            <i class="bi bi-arrow-clockwise"></i>
            Reiniciar Tour
          </div>
          <div class="dropdown-item" @click.stop="showHelp">
            <i class="bi bi-book"></i>
            Documentación
          </div>
        </div>
      </div>
      
      <!-- Menú de usuario -->
      <div class="user-menu" @click="toggleDropdown">
        <span class="user-name">{{ userName }}</span>
        <div class="user-avatar">{{ userInitial }}</div>
        <i class="bi bi-chevron-down dropdown-arrow" :class="{ 'rotated': showDropdown }"></i>
        <div class="dropdown-menu" :class="{ 'show': showDropdown }">
          <div class="dropdown-item" @click.stop="logout">
            <i class="bi bi-box-arrow-right"></i>
            Cerrar sesión
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'TopBar',
  data() {
    return {
      showDropdown: false,
      showHelpDropdown: false
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user'
    }),
    pageTitle() {
      const routeName = this.$route.name
      const titles = {
        'dashboard': 'Dashboard',
        'ingresos': 'Gestión de Ingresos',
        'despachos': 'Gestión de Despachos',
        'trazabilidad': 'Gestión de Trazabilidad',
        'inventario': 'Gestión de Inventario',
        'analytics': 'Análisis',
        'cost-management': 'Gestión de Costos',
        'instalaciones': 'Instalaciones',
        'orders': 'Gestión de Órdenes',
        'operator': 'Dashboard del Operario',
        'picking-assignment': 'Asignación de Picking',
        'users': 'Gestión de Usuarios'
      }
      return titles[routeName] || 'Trazalogis WMS'
    },
    userName() {
      return this.user?.name || this.user?.username || 'Usuario'
    },
    userInitial() {
      return this.userName.charAt(0).toUpperCase()
    }
  },
  methods: {
    ...mapActions({
      logoutAction: 'auth/logout',
      startTourAction: 'tour/startTour'
    }),
    toggleSidebar() {
      this.$emit('toggle-sidebar')
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown
      this.showHelpDropdown = false // Cerrar el otro dropdown
    },
    toggleHelpDropdown() {
      this.showHelpDropdown = !this.showHelpDropdown
      this.showDropdown = false // Cerrar el otro dropdown
    },
    startTour() {
      this.showHelpDropdown = false
      // Usar Vuex para manejar el estado del tour
      try {
        this.startTourAction()
      } catch (error) {
        console.error('Error starting tour from TopBar:', error)
      }
    },
    resetTour() {
      this.showHelpDropdown = false
      // Emitir evento global para resetear el tour de manera más segura
      try {
        if (this.$root) {
          this.$root.$emit('reset-tour')
        }
      } catch (error) {
        console.error('Error resetting tour from TopBar:', error)
      }
    },
    showHelp() {
      this.showHelpDropdown = false
      // Abrir documentación en nueva pestaña
      try {
        window.open('https://docs.trazalogis.com', '_blank')
      } catch (error) {
        console.error('Error opening help documentation:', error)
      }
    },
    logout() {
      this.showDropdown = false
      this.logoutAction()
        .then(() => {
          this.$router.push('/login')
        })
        .catch(error => {
          console.error('Error al cerrar sesión:', error)
          // Forzar redirección incluso si hay error
          this.$router.push('/login')
        })
    },
    handleClickOutside(event) {
      // Verificar que el elemento y sus propiedades existen antes de acceder
      if (!event || !event.target || !this.$el) {
        return
      }
      
      try {
        if (!this.$el.contains(event.target)) {
          this.showDropdown = false
          this.showHelpDropdown = false
        }
      } catch (error) {
        console.warn('Error handling click outside:', error)
        // Cerrar dropdowns como fallback
        this.showDropdown = false
        this.showHelpDropdown = false
      }
    }
  },
  mounted() {
    // Cerrar dropdowns al hacer clic fuera de manera más segura
    try {
      document.addEventListener('click', this.handleClickOutside)
    } catch (error) {
      console.warn('Error adding click listener:', error)
    }
  },
  beforeUnmount() {
    // Limpiar event listener de manera más segura
    try {
      document.removeEventListener('click', this.handleClickOutside)
    } catch (error) {
      console.warn('Error removing click listener:', error)
    }
  }
}
</script>

<style scoped>
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 20px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  z-index: 900;
}

.menu-toggle {
  display: none;
  cursor: pointer;
  font-size: 1.5rem;
  color: #333;
}

.topbar-title h2 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.help-menu {
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.help-menu:hover {
  background-color: #f0f8ff;
}

.help-icon {
  font-size: 20px;
  color: #1976d2;
  transition: color 0.2s ease;
}

.help-menu:hover .help-icon {
  color: #1565c0;
}

.help-dropdown {
  position: absolute;
  top: 45px;
  right: 0;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.2s ease;
  z-index: 1000;
  border: 1px solid #e0e0e0;
}

.help-dropdown.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.user-menu {
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.user-menu:hover {
  background-color: #f5f5f5;
}

.user-name {
  margin-right: 10px;
  font-weight: 500;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #3f51b5;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-right: 8px;
}

.dropdown-arrow {
  font-size: 12px;
  color: #666;
  transition: transform 0.2s ease;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 45px;
  right: 0;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.2s ease;
  z-index: 1000;
}

.dropdown-menu.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  padding: 12px 16px;
  color: #333;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item i {
  font-size: 14px;
  width: 16px;
  text-align: center;
}

/* Responsive */
@media (max-width: 768px) {
  .menu-toggle {
    display: block;
    padding: 8px;
    border-radius: 4px;
    transition: background-color 0.2s ease;
  }
  
  .menu-toggle:hover {
    background-color: #f5f5f5;
  }
  
  .topbar {
    padding: 0 15px;
  }
  
  .topbar-title h2 {
    font-size: 1.1rem;
  }
  
  .user-name {
    display: none;
  }
  
  .topbar-actions {
    gap: 8px;
  }
  
  .help-dropdown,
  .dropdown-menu {
    right: -10px;
    min-width: 180px;
  }
  
  .user-avatar {
    width: 32px;
    height: 32px;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .topbar {
    padding: 0 10px;
  }
  
  .topbar-title h2 {
    font-size: 1rem;
  }
  
  .help-dropdown,
  .dropdown-menu {
    right: -15px;
    min-width: 160px;
    font-size: 0.9rem;
  }
  
  .dropdown-item {
    padding: 10px 12px;
    font-size: 0.85rem;
  }
}

/* Animaciones */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.help-menu:active {
  animation: pulse 0.2s ease;
}
</style>
